import { RegisterData, FraudCheck, IPRegistration } from '../types'

export class FraudDetection {
  private static readonly RISK_THRESHOLDS = {
    HIGH_RISK: 80,
    MEDIUM_RISK: 50,
    MAX_IP_REGISTRATIONS: 3,
    RAPID_REGISTRATION_WINDOW: 60 * 60 * 1000, // 1 hour
    SUSPICIOUS_EMAIL_DOMAINS: [
      '10minutemail.com',
      'tempmail.org',
      'guerrillamail.com',
      'mailinator.com',
      'throwaway.email'
    ]
  }

  static async getUserIP(): Promise<string> {
    try {
      // For demo purposes, we'll use a simple method
      // In production, you'd use a proper IP detection service
      return '192.168.1.1' // Placeholder
    } catch (error) {
      return 'unknown'
    }
  }

  static calculateRiskScore(data: RegisterData): { score: number; factors: string[] } {
    let score = 0
    const factors: string[] = []

    // Check email domain
    if (this.isSuspiciousEmail(data.email)) {
      score += 40
      factors.push('Disposable email domain')
    }

    // Check for generic usernames
    if (this.isGenericUsername(data.username)) {
      score += 20
      factors.push('Generic username pattern')
    }

    // Check referral code usage
    if (data.referralCode) {
      score += 10 // Referrals are inherently riskier
      factors.push('Using referral code')
    }

    // Check registration timing (rapid registrations)
    const ipRegistrations = this.getIPRegistrations(data.ipAddress || '')
    if (ipRegistrations.registrationCount >= this.RISK_THRESHOLDS.MAX_IP_REGISTRATIONS) {
      score += 50
      factors.push(`Multiple registrations from same IP (${ipRegistrations.registrationCount})`)
    }

    // Check for recent registrations from same IP
    if (this.hasRecentRegistrations(data.ipAddress || '')) {
      score += 30
      factors.push('Recent registration from same IP')
    }

    return { score, factors }
  }

  static performFraudCheck(data: RegisterData): FraudCheck {
    const { score, factors } = this.calculateRiskScore(data)
    
    let status: 'approved' | 'flagged' | 'blocked' = 'approved'
    let manualReview = false

    if (score >= this.RISK_THRESHOLDS.HIGH_RISK) {
      status = 'blocked'
      manualReview = true
    } else if (score >= this.RISK_THRESHOLDS.MEDIUM_RISK) {
      status = 'flagged'
      manualReview = true
    }

    const fraudCheck: FraudCheck = {
      userId: `temp_${Date.now()}`, // Will be updated with real user ID
      ipAddress: data.ipAddress || 'unknown',
      userAgent: data.userAgent || 'unknown',
      registrationTime: data.registrationTimestamp || new Date(),
      referralCode: data.referralCode,
      riskScore: score,
      riskFactors: factors,
      status,
      manualReview
    }

    // Store fraud check result
    this.storeFraudCheck(fraudCheck)
    
    // Update IP registration tracking
    this.trackIPRegistration(data.ipAddress || '', fraudCheck.userId)

    return fraudCheck
  }

  private static isSuspiciousEmail(email: string): boolean {
    const domain = email.split('@')[1]?.toLowerCase()
    if (!domain) return true

    return this.RISK_THRESHOLDS.SUSPICIOUS_EMAIL_DOMAINS.includes(domain) ||
           domain.includes('temp') ||
           domain.includes('disposable') ||
           /^\d+/.test(domain) // Domains starting with numbers
  }

  private static isGenericUsername(username: string): boolean {
    const genericPatterns = [
      /^user\d+$/i,
      /^test\d*$/i,
      /^anonymous\d*$/i,
      /^guest\d*$/i,
      /^temp\d*$/i,
      /^fake\d*$/i
    ]

    return genericPatterns.some(pattern => pattern.test(username))
  }

  private static getIPRegistrations(ipAddress: string): IPRegistration {
    const stored = localStorage.getItem('ipRegistrations')
    const ipRegistrations: IPRegistration[] = stored ? JSON.parse(stored) : []
    
    return ipRegistrations.find(ip => ip.ipAddress === ipAddress) || {
      ipAddress,
      userIds: [],
      registrationCount: 0,
      lastRegistration: new Date(0),
      flagged: false
    }
  }

  private static hasRecentRegistrations(ipAddress: string): boolean {
    const ipReg = this.getIPRegistrations(ipAddress)
    const timeDiff = Date.now() - ipReg.lastRegistration.getTime()
    return timeDiff < this.RISK_THRESHOLDS.RAPID_REGISTRATION_WINDOW && ipReg.registrationCount > 0
  }

  private static storeFraudCheck(fraudCheck: FraudCheck): void {
    const stored = localStorage.getItem('fraudChecks')
    const fraudChecks: FraudCheck[] = stored ? JSON.parse(stored) : []
    fraudChecks.push(fraudCheck)
    localStorage.setItem('fraudChecks', JSON.stringify(fraudChecks))
  }

  private static trackIPRegistration(ipAddress: string, userId: string): void {
    const stored = localStorage.getItem('ipRegistrations')
    const ipRegistrations: IPRegistration[] = stored ? JSON.parse(stored) : []
    
    const existingIndex = ipRegistrations.findIndex(ip => ip.ipAddress === ipAddress)
    
    if (existingIndex >= 0) {
      ipRegistrations[existingIndex].userIds.push(userId)
      ipRegistrations[existingIndex].registrationCount++
      ipRegistrations[existingIndex].lastRegistration = new Date()
      
      if (ipRegistrations[existingIndex].registrationCount >= this.RISK_THRESHOLDS.MAX_IP_REGISTRATIONS) {
        ipRegistrations[existingIndex].flagged = true
      }
    } else {
      ipRegistrations.push({
        ipAddress,
        userIds: [userId],
        registrationCount: 1,
        lastRegistration: new Date(),
        flagged: false
      })
    }
    
    localStorage.setItem('ipRegistrations', JSON.stringify(ipRegistrations))
  }

  static getFraudChecks(): FraudCheck[] {
    const stored = localStorage.getItem('fraudChecks')
    return stored ? JSON.parse(stored) : []
  }

  static getFlaggedRegistrations(): FraudCheck[] {
    return this.getFraudChecks().filter(check => check.status !== 'approved')
  }

  static approveRegistration(userId: string): void {
    const fraudChecks = this.getFraudChecks()
    const checkIndex = fraudChecks.findIndex(check => check.userId === userId)
    
    if (checkIndex >= 0) {
      fraudChecks[checkIndex].status = 'approved'
      fraudChecks[checkIndex].manualReview = false
      localStorage.setItem('fraudChecks', JSON.stringify(fraudChecks))
    }
  }

  static blockRegistration(userId: string): void {
    const fraudChecks = this.getFraudChecks()
    const checkIndex = fraudChecks.findIndex(check => check.userId === userId)
    
    if (checkIndex >= 0) {
      fraudChecks[checkIndex].status = 'blocked'
      localStorage.setItem('fraudChecks', JSON.stringify(fraudChecks))
    }
  }
}